import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getKcClsx } from 'keycloakify/login/lib/kcClsx';
import type { PageProps } from 'keycloakify/login/pages/PageProps';
import ArrowLeft16 from '@bankiru/icons/react/ArrowLeft16';
import ArrowLeft24 from '@bankiru/icons/react/ArrowLeft24';
import { Flexbox } from '@bankiru/ui-kit/components/Flexbox';
import Button from '@bankiru/ui-kit/core/Button';
import Text from '@bankiru/ui-kit/core/Text';

import '../../main.css';

import { WebvisorDataHidingContainer } from '../../../components/WebvisorDataHidingContainer/WebvisorDataHidingContainer';
import { ThemeContext } from '../../../context/ThemeContextWrapper';
import { formatCountdown } from '../../../helpers/formatCountdown';
import { formatPhoneNumber } from '../../../helpers/formatPhoneNumber';
import { getInputStatus } from '../../../helpers/getInputStatus';
import { useCountdown } from '../../../hooks/useCountdown';
import { updateAuthTypeSelected } from '../../helpers';
import type { I18n } from '../../i18n';
import type { KcContext } from '../../KcContext';
import { SplittedCodeInput } from './components/SplittedCodeInput/SplittedCodeInput';

const RESEND_TIMEOUT = 60;
const CONFIRMATION_CODE_LENGTH = 4;

export default function LoginSms(
	props: PageProps<Extract<KcContext, { pageId: 'login-sms.ftl' }>, I18n>,
) {
	const { isMobile } = useContext(ThemeContext);

	const { kcContext, i18n, doUseDefaultCss, Template, classes } = props;

	const [isInputChanged, setIsInputChanged] = useState(false);

	const formRef = useRef<HTMLFormElement | null>(null);

	const { kcClsx } = getKcClsx({
		doUseDefaultCss,
		classes,
	});

	const { realm, url, usernameHidden, auth, registrationDisabled, messagesPerField } = kcContext;
	const { msg, msgStr } = i18n;

	const submitRef = useRef<HTMLInputElement | null>(null);

	const [isLoginButtonDisabled, setIsLoginButtonDisabled] = useState(false);
	const [inputValue, setInputValue] = useState('');

	const resendTime = useCountdown(RESEND_TIMEOUT);

	useEffect(() => updateAuthTypeSelected(false), []);

	const handleInputChange = useCallback((value: string) => {
		setIsInputChanged(true);
		setInputValue(value);
	}, []);

	useEffect(() => {
		if (inputValue.length === CONFIRMATION_CODE_LENGTH) {
			setIsLoginButtonDisabled(true);
			if (formRef.current?.requestSubmit) {
				formRef.current.requestSubmit();
			} else {
				formRef.current?.submit();
			}
		}
	}, [inputValue]);

	return (
		<Template
			kcContext={kcContext}
			i18n={i18n}
			doUseDefaultCss={doUseDefaultCss}
			classes={classes}
			displayMessage={!messagesPerField.existsError('totp')}
			headerNode={
				<Flexbox
					flexDirection="row"
					alignItems="center"
					justifyContent={isMobile ? 'center' : 'flex-start'}
				>
					{msg('loginHeaderOtp')}
				</Flexbox>
			}
			displayInfo={realm.password && realm.registrationAllowed && !registrationDisabled}
			infoNode={
				<div id="kc-registration-container">
					<div id="kc-registration">
						<span>
							{msg('noAccount')}{' '}
							<a tabIndex={8} href={url.registrationUrl}>
								{msg('doRegister')}
							</a>
						</span>
					</div>
				</div>
			}
		>
			<div id="kc-form">
				<div id="kc-form-wrapper" className="kcFormWrapper">
					{realm.password && (
						<form
							id="kc-form-login"
							onSubmit={() => {
								setIsLoginButtonDisabled(true);
								return true;
							}}
							action={url.loginAction}
							method="post"
							ref={formRef}
						>
							<div className={kcClsx('kcFormGroupClass')}>
								<Flexbox flexDirection="column" alignItems="center" gap="12">
									<Text color="secondary" size="fs16">
										{i18n.msg('smsSent')}
									</Text>
									<WebvisorDataHidingContainer>
										<Text color="primary" size="fs16" weight="medium">
											{formatPhoneNumber(auth?.attemptedUsername)}
										</Text>
									</WebvisorDataHidingContainer>
								</Flexbox>
							</div>
							{!usernameHidden && (
								<div className={kcClsx('kcFormGroupClass')}>
									<SplittedCodeInput
										value={inputValue}
										isDisabled={isLoginButtonDisabled}
										onChange={handleInputChange}
										error={
											!isInputChanged &&
											getInputStatus(messagesPerField, 'totp')?.message
										}
									/>
								</div>
							)}
							<input type="hidden" id="totp" name="totp" value={inputValue} />
							<div id="kc-form-buttons" className={kcClsx('kcFormGroupClass')}>
								<input
									type="hidden"
									id="id-hidden-input"
									name="credentialId"
									value={auth.selectedCredential}
								/>
								<Flexbox gap="16">
									<Button
										skin="white"
										size="h48"
										leftIcon={{
											size24: <ArrowLeft24 />,
											size16: <ArrowLeft16 />,
										}}
										href={url.loginRestartFlowUrl}
										onClick={() => updateAuthTypeSelected(true)}
										isDisabled={isLoginButtonDisabled}
									/>
									<Button
										size="h48"
										type="submit"
										name="submitAction"
										value="resend"
										isDisabled={resendTime > 0}
										isFullWidth
									>
										{resendTime > 0
											? msgStr('resendButton', formatCountdown(resendTime))
											: msgStr('resendButtonActive')}
									</Button>
								</Flexbox>
								<input
									id="submitAction"
									name="submitAction"
									value="submit"
									ref={submitRef}
									hidden
								/>
							</div>
						</form>
					)}
				</div>
			</div>
		</Template>
	);
}
